// frontend/src/hooks/RealtimePoll.js
import axios from 'axios';
import { pollingManager } from '../services/PollingManager';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const channels = new Map();
const lastCheck = new Map();

const getToken = () => localStorage.getItem('access_token');

/**
 * Start polling for a resource
 * @param {string} key - Resource key: 'projects' | 'tasks' | 'comments' | 'notifications' | 'kpi-approvals'
 * @param {Object} params - Additional query parameters
 * @param {Function} callback - Callback function to handle updates
 * @param {number} customInterval - Custom polling interval in ms (optional)
 * @returns {string} The polling key
 */
export const start = (key, params = {}, callback = null, customInterval = null) => {
  // Set custom interval if provided
  if (customInterval) {
    pollingManager.setInterval(key, customInterval);
  }

  // Initialize lastCheck timestamp (except for kpi-approvals which doesn't use it)
  if (key !== 'kpi-approvals' && !lastCheck.has(key)) {
    lastCheck.set(key, '1970-01-01T00:00:00Z');
  }

  const fetchUpdates = async () => {
    try {
      const token = getToken();
      if (!token) return;

      let url = '';
      let useTimestamp = true;

      // Define endpoint URLs
      switch (key) {
        case 'notifications':
          url = `${API_BASE}/notifications/updated-since`;
          break;
        case 'projects':
          url = `${API_BASE}/projects/updated-since`;
          break;
        case 'tasks':
          url = `${API_BASE}/tasks/updated-since`;
          break;
        case 'comments':
          url = `${API_BASE}/comments/updated-since`;
          break;
        case 'kpi-approvals':
          url = `${API_BASE}/users/kpis/pending-approvals`;
          useTimestamp = false;
          break;
        default:
          console.warn(`[RealtimePoll] Unknown key: ${key}`);
          return;
      }

      // Build request params
      const requestParams = useTimestamp
        ? { ...params, timestamp: lastCheck.get(key) }
        : { ...params };

      const res = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json',
        },
        params: requestParams,
        timeout: 10000,
      });

      // Parse response data
      let dataArray = [];
      let isKpiApprovals = false;

      if (key === 'kpi-approvals') {
        // KPI approvals returns { users: [...] }
        dataArray = res.data?.users || [];
        isKpiApprovals = true;
      } else if (Array.isArray(res.data)) {
        dataArray = res.data;
      } else if (res.data?.data && Array.isArray(res.data.data)) {
        dataArray = res.data.data;
      } else if (typeof res.data === 'object' && res.data !== null) {
        // Handle paginated responses
        dataArray = res.data.data || [];
      }

      // Process updates if we have data
      if (dataArray.length > 0) {
        // Call registered callback
        const channel = channels.get(key);
        if (channel?.callback) {
          const callbackData = isKpiApprovals ? res.data : dataArray;
          channel.callback(callbackData);
        }

        // Update lastCheck timestamp for timestamp-based endpoints
        if (useTimestamp) {
          const latest = dataArray[dataArray.length - 1];
          const timestamp = latest?.updated_at || latest?.created_at;
          if (timestamp) {
            lastCheck.set(key, timestamp);
          } else {
            lastCheck.set(key, new Date().toISOString());
          }
        }
      }
    } catch (err) {
      // Don't log 401/403 as errors (expected when token expires)
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        console.warn(`[RealtimePoll] ${key} fetch error:`, err.message);
      }
    }
  };

  // Store callback if provided
  if (callback) {
    channels.set(key, { callback });
  }

  // Start polling via PollingManager
  pollingManager.start(key, fetchUpdates, params, customInterval);

  // Execute immediately on start
  fetchUpdates();

  return key;
};

/**
 * Stop polling for a resource
 * @param {string} key - Resource key
 */
export const stop = (key) => {
  pollingManager.stop(key);
  channels.delete(key);
  lastCheck.delete(key);
};

/**
 * Pause polling for a resource (timer continues but won't execute)
 * @param {string} key - Resource key
 */
export const pause = (key) => {
  pollingManager.pause(key);
};

/**
 * Resume polling for a resource
 * @param {string} key - Resource key
 */
export const resume = (key) => {
  pollingManager.resume(key);
};

/**
 * Register a callback for a resource
 * @param {string} key - Resource key
 * @param {Function} callback - Callback function
 */
export const on = (key, callback) => {
  if (!channels.has(key)) {
    channels.set(key, {});
  }
  channels.get(key).callback = callback;
};

/**
 * Remove callback for a resource
 * @param {string} key - Resource key
 */
export const off = (key) => {
  if (channels.has(key)) {
    channels.get(key).callback = null;
  }
};

/**
 * Manually advance the last check timestamp (forces fresh fetch)
 * @param {string} key - Resource key
 */
export const advanceLastCheck = (key) => {
  lastCheck.set(key, new Date().toISOString());
  console.log(`[RealtimePoll] ${key} lastCheck advanced →`, lastCheck.get(key));
  
  // Trigger immediate poll if active
  pollingManager.advanceLastCheck(key);
};

/**
 * Get the last check timestamp for a resource
 * @param {string} key - Resource key
 * @returns {string|null} ISO timestamp or null
 */
export const getLastCheck = (key) => {
  return lastCheck.get(key) || null;
};

/**
 * Get polling status for a resource
 * @param {string} key - Resource key
 * @returns {Object} Polling status info
 */
export const getStatus = (key) => {
  return pollingManager.getPollStatus(key);
};

// Export pollingManager for direct access if needed
export { pollingManager };

// Default export for convenience
export default {
  start,
  stop,
  pause,
  resume,
  on,
  off,
  advanceLastCheck,
  getLastCheck,
  getStatus,
  pollingManager,
};